// src/pages/Admin/Users.jsx
import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Menu,
  MenuItem,
  Avatar,
  Badge,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Alert,
  LinearProgress,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Search,
  FilterList,
  Refresh,
  MoreVert,
  Visibility,
  Edit,
  Delete,
  Person,
  Email,
  Phone,
  LocationOn,
  Business,
  VerifiedUser,
  Block,
  CheckCircle,
  Warning,
  ExpandMore,
  Add,
  Lock,
  LockOpen,
  History,
  ShoppingCart,
  LocalShipping,
  AttachMoney,
  Chat,
  ArrowForward,
  CalendarToday,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import { formatDate, formatDateTime, getStatusColor } from '../../utils/helpers';
import toast from 'react-hot-toast';

const AdminUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [tabValue, setTabValue] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editDialog, setEditDialog] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [resetPasswordDialog, setResetPasswordDialog] = useState(null);
  const [viewDialog, setViewDialog] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);
  const [userStats, setUserStats] = useState({});

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAllUsers();
      setUsers(response.data.users || []);
      setUserStats(response.data.stats || {});
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    const roleMap = ['all', 'buyer', 'admin', 'sales', 'shipping'];
    setRoleFilter(roleMap[newValue]);
  };

  const handleStatusToggle = async (user) => {
    try {
      const newStatus = user.status === 'active' ? 'suspended' : 'active';
      await adminAPI.updateUser(user._id, { status: newStatus });
      toast.success(`User ${newStatus === 'active' ? 'activated' : 'suspended'}`);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user status:', error);
      toast.error('Failed to update user status');
    }
  };

  const handleRoleUpdate = async (userId, newRole) => {
    try {
      await adminAPI.updateUser(userId, { role: newRole });
      toast.success(`User role updated to ${newRole}`);
      fetchUsers();
      setEditDialog(null);
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error('Failed to update user role');
    }
  };

  const handleDeleteUser = async () => {
    try {
      await adminAPI.deleteUser(deleteDialog._id);
      toast.success('User deleted successfully');
      fetchUsers();
      setDeleteDialog(null);
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    }
  };

  const handleResetPassword = async () => {
    try {
      // Call API to reset password
      toast.success('Password reset email sent');
      setResetPasswordDialog(null);
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error('Failed to reset password');
    }
  };

  const stats = userStats;

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.company?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
    return matchesSearch && matchesRole && matchesStatus;
  });

  if (loading) {
    return (
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <LinearProgress sx={{ mb: 2 }} />
        <Typography>Loading users...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Users Management
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage all user accounts and permissions
          </Typography>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchUsers}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {}}
          >
            Add User
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Total Users
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.total || 0}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: '#2196f315', color: '#2196f3' }}>
                  <Person />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Active Users
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {stats.active || 0}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: '#4caf5015', color: '#4caf50' }}>
                  <CheckCircle />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    Verified Buyers
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="info.main">
                    {stats.verified || 0}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: '#00bcd415', color: '#00bcd4' }}>
                  <VerifiedUser />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" variant="body2">
                    New This Month
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="warning.main">
                    {stats.newThisMonth || 0}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: '#ff980015', color: '#ff9800' }}>
                  <Add />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabs */}
      <Paper sx={{ mb: 3, borderRadius: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="All Users" icon={<Person />} iconPosition="start" />
          <Tab label="Buyers" icon={<ShoppingCart />} iconPosition="start" />
          <Tab label="Admins" icon={<VerifiedUser />} iconPosition="start" />
          <Tab label="Sales Team" icon={<Chat />} iconPosition="start" />
          <Tab label="Shipping Team" icon={<LocalShipping />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Search and Filters */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search by name, email, or company..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
          </Grid>
          
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                label="Status"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="suspended">Suspended</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="unverified">Unverified</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Join Date</InputLabel>
              <Select label="Join Date" defaultValue="all">
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="week">This Week</MenuItem>
                <MenuItem value="month">This Month</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={2}>
            <Button
              fullWidth
              startIcon={<FilterList />}
              variant="outlined"
              onClick={() => {}}
            >
              More Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Users Table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead sx={{ bgcolor: 'grey.50' }}>
              <TableRow>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">User</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">Role</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">Verification</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">Status</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">Joined</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="subtitle2" fontWeight="bold">Actions</Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Person sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary">
                        No users found
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <React.Fragment key={user._id}>
                    <TableRow hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 40, height: 40 }}>
                            {user.name?.charAt(0) || 'U'}
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight="medium">
                              {user.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {user.email}
                            </Typography>
                            <br />
                            <Typography variant="caption" color="text.secondary">
                              {user.company || 'No company'}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.role}
                          size="small"
                          color={
                            user.role === 'admin' ? 'error' :
                            user.role === 'buyer' ? 'primary' :
                            user.role === 'sales' ? 'info' :
                            user.role === 'shipping' ? 'warning' : 'default'
                          }
                          sx={{ textTransform: 'capitalize', fontWeight: 'medium' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip
                            label={user.verificationStatus || 'unverified'}
                            size="small"
                            color={getStatusColor(user.verificationStatus)}
                            icon={user.verificationStatus === 'verified' ? <VerifiedUser /> : <Warning />}
                          />
                          {user.verificationStatus === 'verified' && (
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(user.verificationDate)}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Switch
                            size="small"
                            checked={user.status === 'active'}
                            onChange={() => handleStatusToggle(user)}
                            color="success"
                          />
                          <Chip
                            label={user.status}
                            size="small"
                            color={getStatusColor(user.status)}
                            sx={{ fontWeight: 'medium' }}
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(user.createdAt)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(user.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => setViewDialog(user)}
                              color="info"
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          
                          <Tooltip title="Edit User">
                            <IconButton
                              size="small"
                              onClick={() => setEditDialog(user)}
                              color="primary"
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Reset Password">
                            <IconButton
                              size="small"
                              onClick={() => setResetPasswordDialog(user)}
                              color="warning"
                            >
                              <Lock />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="Delete User">
                            <IconButton
                              size="small"
                              onClick={() => setDeleteDialog(user)}
                              color="error"
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>

                    {/* Expanded Details */}
                    {expandedUser === user._id && (
                      <TableRow>
                        <TableCell colSpan={6} sx={{ bgcolor: 'grey.50', p: 3 }}>
                          <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                              <Paper sx={{ p: 2 }}>
                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                  User Statistics
                                </Typography>
                                <Grid container spacing={2}>
                                  <Grid item xs={6}>
                                    <Box sx={{ textAlign: 'center' }}>
                                      <Typography variant="caption" color="textSecondary">
                                        Total Orders
                                      </Typography>
                                      <Typography variant="h6" fontWeight="bold">
                                        {user.stats?.totalOrders || 0}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Box sx={{ textAlign: 'center' }}>
                                      <Typography variant="caption" color="textSecondary">
                                        Total Spent
                                      </Typography>
                                      <Typography variant="h6" fontWeight="bold" color="success.main">
                                        ${user.stats?.totalSpent || 0}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Box sx={{ textAlign: 'center' }}>
                                      <Typography variant="caption" color="textSecondary">
                                        Active Shipments
                                      </Typography>
                                      <Typography variant="h6" fontWeight="bold" color="info.main">
                                        {user.stats?.activeShipments || 0}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  <Grid item xs={6}>
                                    <Box sx={{ textAlign: 'center' }}>
                                      <Typography variant="caption" color="textSecondary">
                                        Messages
                                      </Typography>
                                      <Typography variant="h6" fontWeight="bold" color="warning.main">
                                        {user.stats?.messages || 0}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Paper>
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <Paper sx={{ p: 2 }}>
                                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                  Recent Activity
                                </Typography>
                                <List dense>
                                  {user.recentActivity?.map((activity, index) => (
                                    <ListItem key={index}>
                                      <ListItemText
                                        primary={activity.action}
                                        secondary={formatDateTime(activity.timestamp)}
                                      />
                                    </ListItem>
                                  ))}
                                </List>
                              </Paper>
                            </Grid>
                          </Grid>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* View User Dialog */}
      <Dialog
        open={Boolean(viewDialog)}
        onClose={() => setViewDialog(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          User Details: {viewDialog?.name}
        </DialogTitle>
        <DialogContent>
          {viewDialog && (
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={4}>
                <Box sx={{ textAlign: 'center' }}>
                  <Avatar
                    sx={{ 
                      width: 120, 
                      height: 120, 
                      fontSize: 40,
                      mx: 'auto',
                      mb: 2
                    }}
                  >
                    {viewDialog.name?.charAt(0) || 'U'}
                  </Avatar>
                  <Typography variant="h6">{viewDialog.name}</Typography>
                  <Typography variant="body2" color="text.secondary">{viewDialog.email}</Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={8}>
                <List>
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar>
                        <Business />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary="Company"
                      secondary={viewDialog.company || 'Not specified'}
                    />
                  </ListItem>
                  <Divider component="li" />
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar>
                        <Phone />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary="Phone"
                      secondary={viewDialog.phone || 'Not specified'}
                    />
                  </ListItem>
                  <Divider component="li" />
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar>
                        <LocationOn />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary="Address"
                      secondary={viewDialog.address || 'Not specified'}
                    />
                  </ListItem>
                  <Divider component="li" />
                  <ListItem>
                    <ListItemAvatar>
                      <Avatar>
                        <CalendarToday />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary="Member Since"
                      secondary={formatDate(viewDialog.createdAt)}
                    />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialog(null)}>Close</Button>
          <Button 
            variant="contained"
            onClick={() => {
              setViewDialog(null);
              setEditDialog(viewDialog);
            }}
          >
            Edit User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog
        open={Boolean(editDialog)}
        onClose={() => setEditDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit User: {editDialog?.name}</DialogTitle>
        <DialogContent>
          {editDialog && (
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Name"
                defaultValue={editDialog.name}
                size="small"
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Email"
                defaultValue={editDialog.email}
                size="small"
                sx={{ mb: 2 }}
              />
              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Role</InputLabel>
                <Select
                  label="Role"
                  defaultValue={editDialog.role}
                >
                  <MenuItem value="buyer">Buyer</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="sales">Sales Team</MenuItem>
                  <MenuItem value="shipping">Shipping Team</MenuItem>
                  <MenuItem value="support">Support</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  defaultValue={editDialog.status}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="suspended">Suspended</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(null)}>Cancel</Button>
          <Button 
            onClick={() => handleRoleUpdate(editDialog?._id, 'admin')}
            variant="contained"
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog
        open={Boolean(deleteDialog)}
        onClose={() => setDeleteDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          {deleteDialog && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                This action cannot be undone. All user data will be permanently deleted.
              </Alert>
              <DialogContentText>
                Are you sure you want to delete user <strong>{deleteDialog.name}</strong>?
                <br />
                <Typography variant="caption" color="text.secondary">
                  This will delete all associated enquiries, messages, and data.
                </Typography>
              </DialogContentText>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)}>Cancel</Button>
          <Button 
            onClick={handleDeleteUser}
            color="error"
            variant="contained"
          >
            Delete User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog
        open={Boolean(resetPasswordDialog)}
        onClose={() => setResetPasswordDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          {resetPasswordDialog && (
            <>
              <DialogContentText gutterBottom>
                Send password reset email to <strong>{resetPasswordDialog.email}</strong>?
              </DialogContentText>
              <Alert severity="info" sx={{ mt: 2 }}>
                User will receive an email with instructions to reset their password.
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetPasswordDialog(null)}>Cancel</Button>
          <Button 
            onClick={handleResetPassword}
            variant="contained"
          >
            Send Reset Email
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminUsers;